import { type PublicFailure } from '@shared/errors'
import type { WorkflowRun } from '@shared/types'
import { create } from 'zustand'

import { clearGenerationDiagnostics } from './errors'
import { toast } from './toast'

export type TrackedGeneration = {
  id: string
  href: string
  label: string
} & (
  | { kind: 'workflow'; workflowId: string; runIds: string[] }
  | { kind: 'playground' | 'video' | 'job'; workflowId?: never; runIds?: never }
)

interface TrackingState {
  accountId: string | null
  session: number
  pending: TrackedGeneration[]
  settled: Set<string>
}

export const useGenerationTracking = create<TrackingState>(() => ({
  accountId: null,
  session: 0,
  pending: [],
  settled: new Set()
}))

export const generationKey = (entry: TrackedGeneration) => `${entry.kind}:${entry.id}`
export const captureGenerationSession = () => useGenerationTracking.getState().session

export function setGenerationAccount(accountId: string | null) {
  const state = useGenerationTracking.getState()
  if (state.accountId === accountId) return
  clearGenerationDiagnostics()
  for (const key of [...state.pending.map(generationKey), ...state.settled]) {
    toast.close(`generation:${key}`)
  }
  useGenerationTracking.setState({ accountId, session: state.session + 1, pending: [], settled: new Set() })
}

export function trackWorkflowGenerations(workflowId: string, runs: WorkflowRun[], session: number) {
  const batches = new Map<string, string[]>()
  for (const run of runs) {
    if (!['pending', 'processing'].includes(run.status)) continue
    const batchId = run.batchId ?? run.runId
    batches.set(batchId, [...(batches.get(batchId) ?? []), run.runId])
  }
  for (const [id, runIds] of batches) {
    trackGeneration(
      { kind: 'workflow', id, workflowId, runIds, href: `/workflow/${workflowId}`, label: 'Workflow generation' },
      session
    )
  }
}

export function trackGeneration(entry: TrackedGeneration, session: number) {
  const state = useGenerationTracking.getState()
  const key = generationKey(entry)
  if (
    !state.accountId ||
    state.session !== session ||
    (state.settled.has(key) && entry.kind !== 'job') ||
    state.pending.some((item) => generationKey(item) === key)
  )
    return
  const settled = new Set(state.settled)
  // Legacy tools deliberately reuse a job row for a newly accepted generation.
  if (entry.kind === 'job') settled.delete(key)
  useGenerationTracking.setState({ pending: [...state.pending, entry], settled })
}

export function forgetGeneration(entry: TrackedGeneration) {
  useGenerationTracking.setState((state) => ({
    pending: state.pending.filter((item) => generationKey(item) !== generationKey(entry))
  }))
}

export interface GenerationOutcome {
  complete: boolean
  total: number
  ready: number
  failed: number
  canceled?: boolean
  failure?: PublicFailure
  warnings?: string[]
}

/** Called only for explicitly tracked submissions, never for historical data. */
export function settleGeneration(
  entry: TrackedGeneration,
  outcome: GenerationOutcome,
  navigate: (href: string) => void
) {
  const state = useGenerationTracking.getState()
  const key = generationKey(entry)
  if (!outcome.complete || !state.pending.some((item) => generationKey(item) === key) || state.settled.has(key)) return
  useGenerationTracking.setState({
    pending: state.pending.filter((item) => generationKey(item) !== key),
    settled: new Set([...state.settled, key])
  })
  const options = {
    id: `generation:${key}`,
    action: {
      label: 'View results',
      onClick: () => {
        navigate(entry.href)
        toast.close(`generation:${key}`)
      }
    },
    description: outcome.failure?.message ?? outcome.warnings?.[0]
  }
  if (outcome.canceled) {
    toast.info(`${entry.label} canceled`, {
      ...options,
      description: 'The provider canceled this generation. Review the details before starting another.'
    })
  } else if (outcome.failed) {
    const title = outcome.ready
      ? `${outcome.ready} of ${outcome.total} images ready; ${outcome.failed} failed`
      : `${entry.label} failed`
    toast.error(title, options)
  } else if (outcome.warnings?.length) {
    toast.info(`${entry.label} ready with a warning`, { ...options, timeout: 0 })
  } else {
    toast.success(`${entry.label} ready`, options)
  }
}
